/** @jsxImportSource @emotion/react */
import { useState, useRef, useCallback } from "react";
import FormularioProducto from "./FormularioProducto";
import CargaMasivaProductos from "./CargaMasivaProductos";
import { descargarPlanillaProductos } from "../BotonDescargarProductos";

import { Toast } from "primereact/toast";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import { confirmDialog } from "primereact/confirmdialog";

import { Boton, Sidebar, TablaFiltrable } from "../../../shared";
import { Titulo } from "../../../shared";
import { accessAPI, downloadFile } from "../../../shared";

export default function IngresoProductos({
  actividad,
  pasoId,
  camposParaTabla,
  productosParaTabla,
  modoLectura,
}) {
  const proceso = actividad.proceso;
  // Usamos los productos completos para tener acceso a toda la información
  const [productos, setProductos] = useState(proceso.producto || []);

  // Productos para mostrar en la tabla (formato plano)
  // Agregar el ID a cada producto de la tabla
  const [productosTabla, setProductosTabla] = useState(() => {
    if (!productosParaTabla || !proceso.producto) return [];
    return productosParaTabla.map((productoTabla, index) => ({
      ...productoTabla,
      id: proceso.producto[index]?.id,
    }));
  });

  const [sideBar, setSideBar] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [formularioKey, setFormularioKey] = useState(0);
  const [tablaKey, setTablaKey] = useState(0);

  // Carga masiva de productos por planilla Excel
  const [loaderPlantilla, setLoaderPlantilla] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [loaderSubirPlantilla, setLoaderSubirPlantilla] = useState(false);
  const [productosPlantilla, setProductosPlantilla] = useState(null);
  const [sideBarPlantilla, setSideBarPlantilla] = useState(false);

  const toast = useRef(null);
  const menuRef = useRef(null);
  const inputPlantillaRef = useRef(null);
  const [menuProductoId, setMenuProductoId] = useState(null);

  // Función helper para construir el objeto para la tabla
  const construirProductoParaTabla = (producto) => {
    const productoParaTabla = { id: producto.id };
    for (const key in camposParaTabla) {
      const campo = producto.producto_x_productocampospropios?.find(
        (cp) => cp.productocampospropios?.id === parseInt(key)
      );
      productoParaTabla[key] = campo?.valor || "";
    }
    return productoParaTabla;
  };

  // Función ejecutada al agregar un producto
  function agregarProductoAProceso(respuesta) {
    // Agregar el producto completo al array de productos
    setProductos((productos) => [...productos, respuesta]);

    // Agregar a la tabla
    const productoParaTabla = construirProductoParaTabla(respuesta);
    setProductosTabla((actual) => [...actual, productoParaTabla]);
  }

  // Función ejecutada al actualizar un producto
  function actualizarProductoEnLista(productoActualizado) {
    // Actualizar el producto completo en el array de productos
    setProductos((productosActuales) => {
      const indiceEncontrado = productosActuales.findIndex(
        (p) => p.id === productoActualizado.id
      );

      if (indiceEncontrado === -1) {
        console.error("Producto no encontrado:", productoActualizado.id);
        return productosActuales;
      }

      const nuevosProductos = productosActuales.map((producto, idx) =>
        idx === indiceEncontrado ? productoActualizado : producto
      );

      // Incrementar la key de la tabla para forzar re-render
      setTablaKey((prev) => prev + 1);

      // Actualizar tabla en el mismo callback para usar el índice correcto
      const productoParaTabla = construirProductoParaTabla(productoActualizado);
      setProductosTabla((tablaActual) =>
        tablaActual.map((producto, index) =>
          index === indiceEncontrado ? productoParaTabla : producto
        )
      );

      return nuevosProductos;
    });
  }

  // Función para abrir el sidebar en modo edición
  const abrirEdicion = useCallback(
    (productoId) => {
      // Buscar el producto por ID (siempre busca en el estado actual)
      const productoCompleto = productos.find((p) => p.id === productoId);

      if (!productoCompleto) {
        console.error("Producto no encontrado con ID:", productoId);
        return;
      }

      setProductoSeleccionado(productoCompleto);
      setModoEdicion(true);
      setFormularioKey((prev) => prev + 1); // Incrementar para forzar re-render del formulario
      setSideBar(true);
    },
    [productos]
  ); // Re-crear cuando productos cambia

  // Función para abrir el sidebar en modo creación
  function abrirCreacion() {
    setProductoSeleccionado(null);
    setModoEdicion(false);
    setFormularioKey((prev) => prev + 1);
    setSideBar(true);
  }

  // Función para abrir el sidebar en modo duplicación
  const abrirDuplicacion = useCallback(
    (productoId) => {
      const productoCompleto = productos.find((p) => p.id === productoId);
      if (!productoCompleto) return;
      setProductoSeleccionado(productoCompleto);
      setModoEdicion(false); // Modo creación, pero con datos populados
      setFormularioKey((prev) => prev + 1);
      setSideBar(true);
    },
    [productos]
  );

  // Función para abrir el sidebar de carga masiva
  function abrirCargaMasiva() {
    setProductosPlantilla(null);
    setSideBarPlantilla(true);
  }

  // Descarga la plantilla Excel con todos los campos del producto
  function descargarPlantilla() {
    setLoaderPlantilla(true);
    // Arma el nombre del archivo con el cliente y el proceso, sin caracteres
    // que no puedan ir en un nombre de archivo
    const nombreArchivo = [
      "Plantilla Productos",
      proceso.cliente?.razonsocial,
      proceso.nombre,
    ]
      .filter(Boolean)
      .join(" - ")
      .replace(/[\\/:*?"<>|]/g, "-");
    downloadFile(
      `colab/actividad/${pasoId}/productos/plantilla`,
      `${nombreArchivo}.xlsx`,
      () => setLoaderPlantilla(false),
      () => {
        setLoaderPlantilla(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "No se pudo descargar la plantilla",
          sticky: true,
        });
      }
    );
  }

  // Botón de Excel de la tabla: en vez de exportar las filas visibles, baja
  // la planilla que arma la API (los emitidos con sus modificaciones
  // pendientes en Notas y, al final, los que todavía no se emitieron)
  function exportarProductos() {
    setExportando(true);
    descargarPlanillaProductos(
      pasoId,
      proceso,
      () => setExportando(false),
      () => {
        setExportando(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "No se pudo descargar la planilla de productos",
          sticky: true,
        });
      }
    );
  }

  // Sube la plantilla completa para que la API la parsee y valide;
  // con la respuesta abre el sidebar de revisión
  function alSeleccionarPlantilla(evento) {
    const archivo = evento.target.files?.[0];
    // Se limpia el value para que volver a elegir el mismo archivo dispare el
    // onChange igual
    evento.target.value = "";
    if (!archivo) return;
    setLoaderSubirPlantilla(true);
    accessAPI(
      "POST",
      `colab/actividad/${pasoId}/productos/plantilla`,
      null,
      (respuesta) => {
        setLoaderSubirPlantilla(false);
        setProductosPlantilla(respuesta.productos);
        setSideBarPlantilla(true);
      },
      (respuesta) => {
        setLoaderSubirPlantilla(false);
        // La validación de la planilla puede devolver varios errores
        const mensajes = Array.isArray(respuesta)
          ? respuesta.map((error) => error.msg).filter(Boolean)
          : [];
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: mensajes.length ? (
            <div css={{ whiteSpace: "pre-line" }}>{mensajes.join("\n")}</div>
          ) : (
            "Ocurrió un error, intente nuevamente más tarde"
          ),
          sticky: true,
        });
      },
      archivo
    );
  }

  // Función ejecutada al terminar la carga masiva de productos
  function alTerminarCargaMasiva(agregados, fallidos) {
    if (fallidos === 0) {
      setSideBarPlantilla(false);
      setProductosPlantilla(null);
      toast.current.show({
        severity: "success",
        summary: "Éxito",
        detail: `${agregados} producto${agregados === 1 ? "" : "s"} agregado${agregados === 1 ? "" : "s"} al proceso`,
        life: 3000,
      });
    } else {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `${fallidos} producto${fallidos === 1 ? "" : "s"} no se pudo${fallidos === 1 ? "" : "ieron"} agregar. Revisá el listado.`,
        sticky: true,
      });
    }
  }

  // Función para eliminar o marcar para eliminar un producto
  function eliminarProducto(productoId) {
    accessAPI(
      "PUT",
      `colab/actividad/${pasoId}/producto/${productoId}/marcareliminar`,
      null,
      (respuesta) => {
        if (respuesta.eliminado) {
          // Producto eliminado directamente, sacarlo de las listas
          setProductos((prev) => prev.filter((p) => p.id !== productoId));
          setProductosTabla((prev) => prev.filter((p) => p.id !== productoId));
          setTablaKey((prev) => prev + 1);
        } else if (respuesta.marcado) {
          // Producto marcado para eliminar, actualizar el estado
          setProductos((prev) =>
            prev.map((p) =>
              p.id === productoId
                ? {
                    ...p,
                    cambioproductocertificado: [
                      ...(p.cambioproductocertificado || []),
                      { bajaproducto: true }
                    ]
                  }
                : p
            )
          );
          setTablaKey((prev) => prev + 1);
        }
        toast.current.show({
          severity: "success",
          summary: "Listo",
          detail: respuesta.msg || "Producto procesado",
          life: 3000
        });
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar producto",
          sticky: true
        });
      }
    );
  }

  // Verifica si un producto tiene cambios pendientes (edición o baja)
  function tieneCambiosPendientes(productoId) {
    const producto = productos.find((p) => p.id === productoId);
    if (!producto) return false;
    return producto.cambioproductocertificado?.length > 0;
  }

  // Construye el texto del tooltip con el detalle de los cambios pendientes
  function construirTooltipCambios(productoId) {
    const producto = productos.find((p) => p.id === productoId);
    if (!producto?.cambioproductocertificado?.length) return "";

    const lineas = [];
    for (const cambio of producto.cambioproductocertificado) {
      if (cambio.bajaproducto) {
        lineas.push("Se eliminará en la próxima emisión");
      } else {
        const etiqueta =
          cambio.producto_x_productocampospropios?.productocampospropios
            ?.etiqueta || "Campo";
        lineas.push(
          `${etiqueta}: ${cambio.valoranterior || "(vacío)"} → ${cambio.valornuevo || "(vacío)"}`
        );
      }
    }
    return lineas.join("\n");
  }

  return (
    <div css={{ position: "relative" }}>
      <Toast ref={toast} position="top-center" />
      <Tooltip key={`tooltip-${tablaKey}`} target=".producto-cambio-tag" />
      <Menu
        model={[
          {
            label: "Editar",
            icon: "pi pi-pen-to-square",
            command: () => abrirEdicion(menuProductoId)
          },
          {
            label: "Duplicar",
            icon: "pi pi-copy",
            command: () => abrirDuplicacion(menuProductoId)
          },
          {
            label: "Eliminar",
            icon: "pi pi-trash",
            command: () =>
              confirmDialog({
                message: "¿Está seguro de que desea eliminar este producto?",
                header: "Confirmar eliminación",
                icon: "pi pi-exclamation-triangle",
                acceptLabel: "Eliminar",
                rejectLabel: "Cancelar",
                acceptClassName: "p-button-danger",
                accept: () => eliminarProducto(menuProductoId)
              })
          }
        ]}
        popup
        ref={menuRef}
      />
      <Sidebar
        visible={sideBar}
        onHide={() => {
          setSideBar(false);
          setModoEdicion(false);
          setProductoSeleccionado(null);
        }}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <FormularioProducto
            key={formularioKey}
            formularioKey={formularioKey}
            proceso={proceso}
            pasoId={pasoId}
            agregarProductoAProceso={agregarProductoAProceso}
            actualizarProductoEnLista={actualizarProductoEnLista}
            modoEdicion={modoEdicion}
            productoSeleccionado={productoSeleccionado}
          />
        </div>
      </Sidebar>
      <input
        type="file"
        ref={inputPlantillaRef}
        accept=".xlsx"
        onChange={alSeleccionarPlantilla}
        css={{ display: "none" }}
      />
      <Sidebar
        visible={sideBarPlantilla}
        onHide={() => {
          setSideBarPlantilla(false);
          setProductosPlantilla(null);
        }}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          {!productosPlantilla && (
            <div>
              <h1>Carga masiva de productos</h1>
              <p css={{ color: "var(--grisTextos)" }}>
                Descargá la plantilla con los campos del producto, completala
                con un producto por fila y subila para revisar los productos
                antes de agregarlos al proceso.
              </p>
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1em",
                  marginTop: "2em",
                }}
              >
                <Boton
                  callback={descargarPlantilla}
                  loader={loaderPlantilla}
                  transparente
                >
                  <i className="pi pi-download" css={{ marginRight: "0.5em" }} />
                  Descargar plantilla
                </Boton>
                <Boton
                  callback={() => inputPlantillaRef.current?.click()}
                  loader={loaderSubirPlantilla}
                >
                  <i className="pi pi-upload" css={{ marginRight: "0.5em" }} />
                  Subir plantilla completa
                </Boton>
              </div>
            </div>
          )}
          {productosPlantilla && (
            <CargaMasivaProductos
              productosIniciales={productosPlantilla}
              camposPropios={
                proceso.certificadotipo?.productocampospropios || []
              }
              pasoId={pasoId}
              agregarProductoAProceso={agregarProductoAProceso}
              alTerminar={alTerminarCargaMasiva}
            />
          )}
        </div>
      </Sidebar>
      <Titulo
        botones={
          <>
            {!modoLectura && (
              <>
                <Boton callback={abrirCargaMasiva} texto>
                  Carga masiva
                </Boton>
                <Boton callback={abrirCreacion} texto icono chico negro>
                  <i className="pi pi-plus"></i>
                </Boton>
              </>
            )}
          </>
        }
      >
        <i className="pi pi-box" css={{ marginRight: "0.5em" }}></i>
        Productos
      </Titulo>
      <TablaFiltrable conFiltros={false}
        exportar={exportarProductos}
        exportando={exportando}
        key={`tabla-productos-${tablaKey}`}
        value={productosTabla}
        emptyMessage="No hay productos cargados"
        size="small"
      >
        {Object.entries(camposParaTabla).map((campo) => {
          return (
            <Column key={campo[0]} field={campo[0]} header={campo[1]}></Column>
          );
        })}
        <Column
          body={(producto) =>
            tieneCambiosPendientes(producto.id) ? (
              <i
                className="pi pi-exclamation-triangle producto-cambio-tag"
                data-pr-tooltip={construirTooltipCambios(producto.id)}
                data-pr-position="top"
                css={{ color: "var(--red-500)", fontSize: "1.1rem" }}
              />
            ) : null
          }
          style={{ width: "80px" }}
        />
        {!modoLectura && (
          <Column
            body={(producto) => (
              <Boton
                texto
                icono
                chico
                negro
                callback={(e) => {
                  setMenuProductoId(producto.id);
                  menuRef.current.toggle(e);
                }}
              >
                <i className="pi pi-ellipsis-v"></i>
              </Boton>
            )}
            style={{ width: "50px" }}
          ></Column>
        )}
      </TablaFiltrable>
    </div>
  );
}
